import axios from 'axios';
import { baseUrl } from '@/config/app';
import { hasToken, getToken, removeToken } from '@/utils/cookies';

// Create an Axios instance
const api = axios.create({
  baseURL: baseUrl.api,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Request Interceptor for setting Authorization headers if needed
api.interceptors.request.use(
  (config) => {
    if (hasToken()) {
      config.headers['Authorization'] = `Bearer ${getToken()}`;
    } else {
      if (hasToken(true)) {
        config.headers['Authorization'] = `Bearer ${getToken(true)}`;
      }
    }
    const path = String(config.url || '');
    if (path.includes('calls/originate')) {
      const at = new Date().toISOString();
      config.headers['X-Voip-Originate'] = '1';
      config.headers['X-Voip-Originate-At'] = at;
      if (import.meta.env.DEV) {
        const fullUrl = `${config.baseURL || ''}${path}`;
        console.info('[api/calls/originate] → POST', fullUrl, config.data);
      }
    }
    return config;
  },
  (error) => {
    Promise.reject(error);
  }
);

// Response Interceptor for handling errors globally
api.interceptors.response.use(
  (response) => response.data,
  (error) => {
    // Handle 429 Rate Limiting
    if (error.response && error.response.status === 429) {
      alert(`Rate Limit Exceeded: ${error.response.data.message}`);
      console.error(
        'Rate limit exceeded:',
        error.response.data.message || 'Too many requests'
      );
    }

    // Handle global errors here (e.g., redirect to login on 401)
    // Skip redirect when the request opted out (e.g. WS token fetch that may fire before auth)
    if (error.response && error.response.status === 401 && !error.config?.skipAuthRedirect) {
      if (error.response.data.message == 'Unauthenticated.') {
        removeToken();
        removeToken(true);
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Log originate XHR in DevTools (registered after the handler above so this runs first on response)
api.interceptors.response.use(
  (response) => {
    const path = String(response.config?.url || '');
    if (path.includes('calls/originate') && import.meta.env.DEV) {
      const fullUrl = `${response.config.baseURL || ''}${path}`;
      console.info('[api/calls/originate] ←', fullUrl, response.status, response.data);
    }
    return response;
  },
  (error) => {
    const path = String(error.config?.url || '');
    if (path.includes('calls/originate') && import.meta.env.DEV) {
      const fullUrl = `${error.config?.baseURL || ''}${path}`;
      console.error(
        '[api/calls/originate] ← error',
        fullUrl,
        error.response?.status,
        error.response?.data
      );
    }
    return Promise.reject(error);
  }
);

export default api;
